import { LitElement, html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';

import translate from '../../utils/webcomponents/translate';

import './portfolio-item';
import portfolioEvents from './portfolio-events';

const inactiveClass = 'is-inactive';
const tableHeader = (showIsinColumn, showCurrencyColumn) => html`
	<div class="table__header">
		<div class="table__heading manageable-list__item-action"></div>
		${showIsinColumn ? html`<div class="table__heading">${translate('watchlist', 'isin', 'ISIN')}</div>` : ''}
		<div class="table__heading">${translate('portfolio', 'name', 'Name')}</div>
		<div class="table__heading text-right text-no-wrap">
			${translate('portfolio', 'purchasedAt', 'Purchased at')}
		</div>
		<div class="table__heading text-right">${translate('portfolio', 'amount', 'Amount')}</div>
		<div class="table__heading text-right">${translate('portfolio', 'invested', 'invested')}</div>
		${showCurrencyColumn
			? html`<div class="table__heading">${translate('portfolio', 'currency', 'Currency')}</div>`
			: ''}
		<div class="table__heading text-right">${translate('portfolio', 'stoploss', 'Stop loss')}</div>
		<div class="table__heading text-right">${translate('portfolio', 'bid', 'Bid')}</div>
		<div class="table__heading text-right">${translate('portfolio', 'ask', 'Ask')}</div>
		<div class="table__heading text-right">${translate('portfolio', 'value', 'Value')}</div>
		<div class="table__heading text-right">${translate('portfolio', 'profit', 'Profit')}</div>
	</div>
`;

class PortfolioTable extends LitElement {
	static get properties() {
		return {
			items: { type: Array },
			isWatchlistActive: { type: Boolean },
			showIsinColumn: { type: Boolean },
			showCurrencyColumn: { type: Boolean },
		};
	}

	render() {
		return html`
			<div class="table-container">
				<div class="table">
					${tableHeader(this.showIsinColumn, this.showCurrencyColumn)}

					<div class="table__body">
						${repeat(
							this.items,
							(item) => item.id,
							(item) => html`
								<x-portfolio-item
									class="table__row manageable-list__item ${item.isProductInactive
										? 'is-inactive'
										: ''}"
									id="${item.id}"
									.isWatchlistActive="${this.isWatchlistActive}"
									.showIsinColumn="${this.showIsinColumn}"
									.showCurrencyColumn="${this.showCurrencyColumn}"
									.data="${item}"
								>
								</x-portfolio-item>
							`,
						)}
					</div>
				</div>
			</div>
		`;
	}

	updated() {
		super.updated();

		if (!this.isDraggable && this.items.length) {
			this.setDraggable();
			this.isDraggable = true;
		}
	}

	dispatchCustomEvent(eventName, detail = {}) {
		const event = new CustomEvent(eventName, {
			bubbles: true,
			detail,
		});

		this.dispatchEvent(event);
	}

	getPortfolioItems() {
		return Array.from(document.querySelectorAll('x-portfolio-item'));
	}

	setDraggable() {
		const domItems = this.querySelectorAll(`x-portfolio-item:not(.${inactiveClass})`);

		domItems.forEach((el) => {
			// Wait for watchlist-items to render
			el.updateComplete.then(() => {
				const dragButton = el.querySelector('.button--drag');

				// Enable watchlist-item drag only when mouse over drag button
				dragButton.addEventListener('mouseover', () => {
					el.setAttribute('draggable', true);
				});
				dragButton.addEventListener('mouseout', () => {
					el.setAttribute('draggable', false);
				});

				el.addEventListener('dragstart', this.dragStart.bind(this));
				el.addEventListener('drop', this.dragStop.bind(this));
				el.addEventListener('dragenter', this.cancelDefault.bind(this));
				el.addEventListener('dragover', this.cancelDefault.bind(this));
			});
		});
	}

	dragStart(e) {
		const domElementIds = [...document.querySelectorAll('x-portfolio-item')].map((t) => t.id);
		const startIndex = domElementIds.indexOf(e.currentTarget.id);

		e.dataTransfer.setData('text/plain', startIndex);
	}

	dragStop(e) {
		this.cancelDefault(e);
		const domElements = this.getPortfolioItems();
		const startIndex = Number(e.dataTransfer.getData('text/plain'));
		const endIndex = domElements.map((t) => t.id).indexOf(e.currentTarget.id);

		if (startIndex !== endIndex) {
			const draggedElement = domElements[startIndex];
			const insertBeforeElementIdx = startIndex < endIndex ? endIndex + 1 : endIndex;
			const insertBeforeElement = domElements[insertBeforeElementIdx];
			const target = domElements[endIndex];
			const dragElements = [draggedElement, target];

			this.setIsDragging(dragElements, true);
			draggedElement.parentElement.insertBefore(draggedElement, insertBeforeElement);
			this.setIsDragging(dragElements, false);

			// Update order property to match visual index
			this.getPortfolioItems().forEach((item, index) => {
				item.data.order = index;
			});

			this.dispatchCustomEvent(portfolioEvents.ITEM_MOVE);
		}
	}

	// Set isDragging flag
	setIsDragging(items, value) {
		items?.forEach((item) => {
			item.isDragging = value;
		});
	}

	cancelDefault(e) {
		e.preventDefault();
		e.stopPropagation();

		return false;
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-portfolio-table', PortfolioTable);

export default PortfolioTable;
